// PREDEFINE SOME USEFUL SHADER FUNCTIONS

export const noiseCode = `
vec3  _s(vec3 i) { return cos(5.*(i+5.*cos(5.*(i.yzx+5.*cos(5.*(i.zxy+5.*cos(5.*i))))))); }
float _t(vec3 i, vec3 u, vec3 a) { return dot(normalize(_s(i + a)), u - a); }
float noise(vec3 p) {
   vec3 i = floor(p), u = p - i, v = 2.*mix(u*u, u*(2.-u)-.5, step(.5,u));
   return mix(mix(mix(_t(i, u, vec3(0.,0.,0.)), _t(i, u, vec3(1.,0.,0.)), v.x),
                  mix(_t(i, u, vec3(0.,1.,0.)), _t(i, u, vec3(1.,1.,0.)), v.x), v.y),
              mix(mix(_t(i, u, vec3(0.,0.,1.)), _t(i, u, vec3(1.,0.,1.)), v.x),
                  mix(_t(i, u, vec3(0.,1.,1.)), _t(i, u, vec3(1.,1.,1.)), v.x), v.y), v.z);
}`;

export const phongCode = `
vec3 phong(vec3 N, vec3 L, vec3 W, vec3 diffuse, vec4 specular) {
   vec3 R = 2. * N * dot(N,L) - L;
   return diffuse      * max(0., dot(N, L)) +
          specular.rgb * pow(max(0.,dot(R,-W)), specular.a);
}
`;

// INITIALIZE THE WEBGL RENDERER

let autodraw = true;
let vertexSize = 6;
export let mesh = {
  triangle_strip: true,
  data: new Float32Array([
     -1, 1,0, 0,0,1,
      1, 1,0, 0,0,1,
     -1,-1,0, 0,0,1,
      1,-1,0, 0,0,1,
   ])
};

export function gl_start(canvas, scene) {
   setTimeout(function() {
      canvas.gl = canvas.getContext('webgl2');
      canvas.gl.width = canvas.width;
      canvas.gl.height = canvas.height;
      
      // Set viewport to match canvas size
      canvas.gl.viewport(0, 0, canvas.width, canvas.height);
      
      // Set global reference immediately
      window.currentGL = canvas.gl;
      
      canvas.setShaders = function(vertexShader, fragmentShader) {
         let gl = this.gl;
         gl.program = gl.createProgram();
         function addshader(type, src) {
            let shader = gl.createShader(type);
            gl.shaderSource(shader, src);
            gl.compileShader(shader);
            if (! gl.getShaderParameter(shader, gl.COMPILE_STATUS))
               console.log('Cannot compile shader:', gl.getShaderInfoLog(shader));
            gl.attachShader(gl.program, shader);
         };

         addshader(gl.VERTEX_SHADER, vertexShader);

         let i = fragmentShader.indexOf('float') + 6;
         addshader(gl.FRAGMENT_SHADER, fragmentShader.substring(0,i)
                                     + noiseCode
                                     + phongCode
                                     + fragmentShader.substring(i));

         gl.linkProgram(gl.program);
         if (! gl.getProgramParameter(gl.program, gl.LINK_STATUS))
            console.log('Could not link the shader program!');
         gl.useProgram(gl.program);
         gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
         gl.enable(gl.DEPTH_TEST);
         gl.depthFunc(gl.LEQUAL);
         vertexMap(['aPos',3,'aNor',3]);
      }
      canvas.setShaders(scene.vertexShader, scene.fragmentShader);
      const intervalId = setInterval(function() {
         if (scene.update)
            scene.update([0,0,7]);
         if (autodraw)
            drawMesh(mesh);
      }, 10);
      
      canvas.stopRender = () => clearInterval(intervalId);
   }, 100);
}

let vertexMap = map => {
   let gl = window.currentGL;
   let vertexAttribute = (name, size, position) => {
      let attr = gl.getAttribLocation(gl.program, name);
      gl.enableVertexAttribArray(attr);
      gl.vertexAttribPointer(attr, size, gl.FLOAT, false, vertexSize * 4, position * 4);
   }
   vertexSize = 0;
   for (let n = 0 ; n < map.length ; n += 2)
      vertexSize += map[n+1];
   let index = 0;
   for (let n = 0 ; n < map.length ; n += 2) {
      vertexAttribute(map[n], map[n+1], index);
      index += map[n+1];
   }
}

// export vertexMap so callers can change attribute layout
export { vertexMap };

let drawMesh = mesh => {
   let gl = window.currentGL;
   gl.bufferData(gl.ARRAY_BUFFER, mesh.data, gl.STATIC_DRAW);
   gl.drawArrays(mesh.triangle_strip ? gl.TRIANGLE_STRIP : gl.TRIANGLES,
                 0, mesh.data.length / vertexSize);
}

export let drawObj = (mesh, matrix, color, customVertexSize) => {
   autodraw = false;
   let gl = window.currentGL;
   let m = mxm(perspective(0,0,-.5),matrix);
   setUniform('Matrix4fv', 'uMF', false, m);
   setUniform('Matrix4fv', 'uMI', false, inverse(m));
   setUniform('3fv', 'uColor', color ?? [1,1,1]);
   
   // Use custom vertex size if provided
   let oldVertexSize = vertexSize;
   if (customVertexSize !== undefined) {
      vertexSize = customVertexSize;
   }
   
   drawMesh(mesh);
   
   // Restore vertex size
   if (customVertexSize !== undefined) {
      vertexSize = oldVertexSize;
   }
}

export let setUniform = (type,name,a,b,c) => {
   let gl = window.currentGL;
   if (!gl) return;
   (gl['uniform'+type])(gl.getUniformLocation(gl.program,name), a,b,c);
}

// ADDING A TEXTURE (exported)
export let addTexture = (index, src) => {
   const gl = window.currentGL;
   if (!gl) return;
   let image = new Image();
   image.crossOrigin = '';
   image.onload = () => {
      // MAKE THIS THE ACTIVE TEXTURE
      gl.activeTexture(gl.TEXTURE0 + index);

      // CREATE A NEW TEXTURE OBJECT
      gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());

      // SPECIFY HOW SRC IMAGE WILL BE LAID OUT
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);

      // FILTERING
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);

      // GENERATE MIPMAP
      gl.generateMipmap(gl.TEXTURE_2D);
   }
   image.src = 'textures/' + src;
}

// Shared global object.
let _ = {};

// SOME USEFUL FUNCTIONS

export let add = (a,b) => { let v = []; for (let i=0 ; i<a.length ; i++) v.push(a[i] + b[i]); return v; }
export let cross = (a,b) => [ a[1]*b[2] - a[2]*b[1], a[2]*b[0] - a[0]*b[2], a[0]*b[1] - a[1]*b[0] ];
export let dot = (a,b) => { let s = 0 ; for (let i=0 ; i<a.length ; i++) s += a[i] * b[i]; return s; }
export let ease = t => { t = Math.max(0, Math.min(1, t)); return t * t * (3 - t - t); }
export let evalBezier = (B,t) => (1-t)*(1-t)*(1-t)*B[0] + 3*(1-t)*(1-t)*t*B[1] + 3*(1-t)*t*t*B[2] + t*t*t*B[3];
export let mix = (a,b,t) => { let c = []; for (let i=0 ; i<a.length ; i++) c[i] = a[i] + t*(b[i]-a[i]); return c; }
export let norm = v => Math.sqrt(dot(v,v));
export let normalize = v => { let s = norm(v); return v.length==3 ? [ v[0]/s,v[1]/s,v[2]/s ] : [ v[0]/s,v[1]/s ]; }
export let resize = (v,s) => v.length==2 ? [ s*v[0], s*v[1] ] : [s*v[0], s*v[1], s*v[2] ];
export let subtract = (a,b) => { let v = []; for (let i=0 ; i<a.length ; i++) v.push(a[i] - b[i]); return v; }

// THIS IS A SIMPLE IMPLEMENTATION OF THE OPERATIONS NEEDED FOR MATRIX MANIPULATION.

let c = t => Math.cos(t);
let s = t => Math.sin(t);
export let identity = () => [1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1];
export let move = (x,y,z) => { if (y===undefined) {z=x[2];y=x[1];x=x[0];}
                        return [1,0,0,0, 0,1,0,0, 0,0,1,0, x,y,z,1]; }
export let turnX = t => [1,0,0,0, 0,c(t),s(t),0, 0,-s(t),c(t),0, 0,0,0,1];
export let turnY = t => [c(t),0,-s(t),0, 0,1,0,0, s(t),0,c(t),0, 0,0,0,1];
export let turnZ = t => [c(t),s(t),0,0, -s(t),c(t),0,0, 0,0,1,0, 0,0,0,1];
export let scale = (x,y,z) => {
   if (y === undefined) y = x;
   if (z === undefined) z = x;
   return [x,0,0,0,  0,y,0,0,  0,0,z,0,  0,0,0,1];
}
export let perspective = (x,y,z) => {
   if (y === undefined) y = x;
   if (z === undefined) z = x;
   return [1,0,0,x,  0,1,0,y,  0,0,1,z,  0,0,0,1];
}

// Multiply two matrices.

export let mxm = (a,b) => {
   let m = [];
   for (let c = 0 ; c < 16 ; c += 4)
   for (let r = 0 ; r < 4 ; r++)
      m.push( a[r]*b[c] + a[r+4]*b[c+1] + a[r+8]*b[c+2] + a[r+12]*b[c+3] );
   return m;
}

// A matrix transforms a point

export let transform = (m,p) => {
   let x = p[0], y = p[1], z = p[2], w = p[3] ?? 1;
   return [
      m[0] * x + m[4] * y + m[ 8] * z + m[12] * w,
      m[1] * x + m[5] * y + m[ 9] * z + m[13] * w,
      m[2] * x + m[6] * y + m[10] * z + m[14] * w,
      m[3] * x + m[7] * y + m[11] * z + m[15] * w,
   ];
}

// Invert a matrix.

export let inverse = src => {
   let dst = [], det = 0, cofactor = (c, r) => {
      let s = (i, j) => src[c+i & 3 | (r+j & 3) << 2];
      return (c+r & 1 ? -1 : 1) * ( (s(1,1)*(s(2,2)*s(3,3)-s(3,2)*s(2,3)))
                                  - (s(2,1)*(s(1,2)*s(3,3)-s(3,2)*s(1,3)))
                                  + (s(3,1)*(s(1,2)*s(2,3)-s(2,2)*s(1,3))) );
   }
   for (let n = 0 ; n < 16 ; n++) dst.push(cofactor(n >> 2, n & 3));
   for (let n = 0 ; n <  4 ; n++) det += src[n] * dst[n << 2];
   for (let n = 0 ; n < 16 ; n++) dst[n] /= det;
   return dst;
}

// Rotation matrix which brings the Z axis to a specified direction.

export let aim = Z => {
   let X = normalize(cross([0,1,0], Z = normalize(Z))),
       Y = normalize(cross(Z, X));
   return [ X[0],X[1],X[2],0, Y[0],Y[1],Y[2],0, Z[0],Z[1],Z[2],0, 0,0,0,1 ];
}

// Transpose a matrix.

export let transpose = m => [
   m[0], m[4], m[8],  m[12],
   m[1], m[5], m[9],  m[13],
   m[2], m[6], m[10], m[14],
   m[3], m[7], m[11], m[15]
];


// NESTABLE MATRIX OBJECT

export function Matrix() {
   let m = [identity()], top = 0;
   this.aim         = Z       => { m[top] = mxm(m[top],aim(Z)); return this; }
   this.call        = proc    => { proc(); return this; }
   this.get         = ()      => m[top];
   this.identity    = ()      => { m[top] = identity(); return this; }
   this.inverse     = ()      => { m[top] = inverse(m[top]); return this; }
   this.move        = (x,y,z) => { m[top] = mxm(m[top],move(x,y,z)); return this; }
   this.perspective = (x,y,z) => { m[top] = mxm(m[top], perspective(x,y,z)); return this; }
   this.pop         = ()      => { if (top > 0) top--; return this; }
   this.push        = ()      => { m[top+1] = m[top].slice(); top++; return this; }
   this.scale       = (x,y,z) => { m[top] = mxm(m[top], scale(x,y,z)); return this; }
   this.set         = matrix  => { m[top] = matrix; return this; }
   this.transform   = p       => { m[top] = transform(m[top],p); return this; }
   this.transpose   = ()      => { m[top] = transpose(m[top]); return this; }
   this.turnX       = a       => { m[top] = mxm(m[top], turnX(a)); return this; }
   this.turnY       = a       => { m[top] = mxm(m[top], turnY(a)); return this; }
   this.turnZ       = a       => { m[top] = mxm(m[top], turnZ(a)); return this; }
}